// src/types/config.ts
import { ITransport, TransportConfig } from './transport';
import { DeviceIdentifyPluginConfig } from './plugins/deviceIdentify';
import { SessionTrackingPluginConfig } from './plugins/sessionTracking';
import { AutoTrackConfig } from '../plugins/AutotrackPlugin/types';
import { ILogger, LogLevel } from '../utils/logger';

/**
 * Special value that indicates a plugin config property should inherit from the top-level SDK config
 */
export const INHERIT_CONFIG = '$inherit' as const;
export type InheritableValue<T> = T | typeof INHERIT_CONFIG;

/**
 * Plugin configurations with inheritance support
 */
export interface PluginConfigurations {
  DeviceIdentifyPlugin?: Partial<DeviceIdentifyPluginConfig> & {
    localStorageKeyName?: InheritableValue<string>;
  };
  SessionTrackingPlugin?: Partial<SessionTrackingPluginConfig> & {
    sessionTimeoutMs?: InheritableValue<number>;
    sessionStorageKeyName?: InheritableValue<string>;
  };
  AutoTrackPlugin?: Partial<AutoTrackConfig> & {
    debugMode?: InheritableValue<boolean>;
    respectDoNotTrack?: InheritableValue<boolean>;
    excludeSelector?: InheritableValue<string>;
    includeSelector?: InheritableValue<string>;
  };
  // Add more plugins as needed
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [pluginName: string]: Record<string, any> | undefined;
}

/**
 * Type for plugin configuration keys
 */
export type PluginConfigKey = keyof PluginConfigurations;

/**
 * Configuration options for the UniqodeInsights SDK.
 */
export interface SdkConfig {
  clientKey: string;
  transport?: ITransport[];
  transportConfig?: TransportConfig[];
  apiEndpoint?: string;
  logLevel?: LogLevel;
  logger?: ILogger;
  pluginOrderConflictStrategy?: 'push' | 'append';
  optOut?: boolean;
  pluginConfig?: PluginConfigurations;
}
